import { useState, useEffect, useCallback } from "react";
import "./dataTable.css";
import orderBy from "lodash/orderBy";
import PropTypes from "prop-types";
import DataRow from "./dataRow";
import TotalRow from "./totalRow";
import CSVExport from "./csvExport";
import { Accordion, Button, Table, Card } from "react-bootstrap";
import { useAccordionButton } from "react-bootstrap/AccordionButton";

function ContextAwareToggle({ children, eventKey, callback }) {
  const accordionOnClick = useAccordionButton(
    eventKey,
    () => callback && callback(eventKey)
  );

  return (
    <Button type="button" variant="link" onClick={accordionOnClick}>
      {children}
    </Button>
  );
}

ContextAwareToggle.propTypes = {
  children: PropTypes.string,
  eventKey: PropTypes.string,
  callback: PropTypes.func,
};

const DataTable = ({
  sortField,
  changeSortOrder,
  data,
  sortDirection,
  dataKeys,
}) => {
  const [selectedSizeFormat, setSelectedSizeFormat] = useState("TB");
  const [selectedSortField, setSelectedSortField] = useState("");

  useEffect(() => {
    setSelectedSortField(sortField);
  }, [sortField]);

  const handleChangeSizeFormat = useCallback(
    (e) => {
      setSelectedSizeFormat(e.target.value);
    },
    [setSelectedSizeFormat]
  );

  const changeSortOrderHandler = useCallback(
    (fieldName) => {
      if (fieldName !== "Interface") {
        setSelectedSortField(fieldName);
        changeSortOrder(fieldName);
      }
    },
    [selectedSortField, changeSortOrder]
  );

  if (!data || data.length === 0) {
    return null;
  }

  let isMonthly = false;
  const dataRows = orderBy(data, sortField, sortDirection).map((item, i) => {
    if (i === 0) isMonthly = "NumDays" in item;
    return (
      <DataRow
        dataKeys={dataKeys}
        selectedSizeFormat={selectedSizeFormat}
        dataItem={item}
        key={i}
      />
    );
  });

  const totalSize = data.reduce(
    (prevVal, item) => prevVal + parseInt(item.Size, 10),
    0
  );
  const totalHits = data.reduce(
    (prevVal, item) => prevVal + parseInt(item.Hits, 10),
    0
  );
  const totalVisitors = data.reduce(
    (prevVal, item) => prevVal + parseInt(item.Visitors, 10),
    0
  );
  const totalViews = data.reduce(
    (prevVal, item) => prevVal + parseInt(item.Views, 10),
    0
  );

  let avgDailySize = null;
  let avgDailyHits = null;
  if (isMonthly) {
    const totalNumDays = data.reduce(
      (prevVal, item) => prevVal + parseInt(item.NumDays, 10),
      0
    );
    avgDailySize = parseFloat(totalSize / totalNumDays);
    avgDailyHits = parseFloat(totalHits / totalNumDays);
  }

  const header = dataKeys.map((dataKey, i) => {
    let th;
    if (dataKey === "Hits") {
      th = (
        <th className="th" key={i}>
          <span
            className={
              dataKey === selectedSortField ? "headerItem active" : "headerItem"
            }
            onClick={() => changeSortOrderHandler(dataKey)}
          >
            {"Downloads/Hits"}
          </span>
        </th>
      );
    } else if (dataKey !== "Size") {
      th = (
        <th className="th" key={i}>
          <span
            className={
              dataKey === selectedSortField ? "headerItem active" : "headerItem"
            }
            onClick={() => changeSortOrderHandler(dataKey)}
          >
            {dataKey}
          </span>
        </th>
      );
    } else {
      th = (
        <th className="th p-3" key={i}>
          <span
            className={
              dataKey === selectedSortField ? "headerItem active" : "headerItem"
            }
            onClick={() => changeSortOrderHandler(dataKey)}
          >
            {dataKey}
          </span>
          <span className="radio-span">
            <input
              type="radio"
              id="tbRadioButton"
              name="size-format"
              value="TB"
              onChange={handleChangeSizeFormat}
              checked={selectedSizeFormat === "TB"}
            />
            <label htmlFor="tbRadioButton">TB</label>
            <input
              type="radio"
              id="gbRadioButton"
              name="size-format"
              value="GB"
              onChange={handleChangeSizeFormat}
              checked={selectedSizeFormat === "GB"}
            />
            <label htmlFor="gbRadioButton">GB</label>
            <input
              type="radio"
              id="mbRadioBtn"
              name="size-format"
              value="MB"
              onChange={handleChangeSizeFormat}
              checked={selectedSizeFormat === "MB"}
            />
            <label htmlFor="mbRadioBtn">MB</label>
          </span>
        </th>
      );
    }
    return th;
  });

  return (
    <div>
      <Accordion defaultActiveKey="0">
        <Card>
          <Card.Header>
            <ContextAwareToggle eventKey="0">Data</ContextAwareToggle>
          </Card.Header>
          <Accordion.Collapse eventKey="0">
            <Card.Body>
              <CSVExport
                data={data}
                dataKeys={dataKeys}
                sortField={sortField}
                sortDirection={sortDirection}
                selectedSizeFormat={selectedSizeFormat}
                totalSize={totalSize}
                totalHits={totalHits}
                totalViews={totalViews}
                totalVisitors={totalVisitors}
                avgDailySize={avgDailySize}
                avgDailyHits={avgDailyHits}
              />
              <Table responsive striped bordered hover>
                <thead className="text-center p-5">
                  <tr>{header}</tr>
                </thead>
                <tbody>
                  {dataRows}
                  <TotalRow
                    dataKeys={dataKeys}
                    selectedSizeFormat={selectedSizeFormat}
                    totalSize={totalSize}
                    totalHits={totalHits}
                    totalViews={totalViews}
                    totalVisitors={totalVisitors}
                    avgDailySize={avgDailySize}
                    avgDailyHits={avgDailyHits}
                  />
                </tbody>
              </Table>
            </Card.Body>
          </Accordion.Collapse>
        </Card>
      </Accordion>
    </div>
  );
};

export default DataTable;

DataTable.propTypes = {
  sortField: PropTypes.string,
  changeSortOrder: PropTypes.func,
  data: PropTypes.array,
  sortDirection: PropTypes.string,
  dataKeys: PropTypes.array,
};